import { BellRing } from 'lucide-react';
import { formatDuration, formatGapDuration } from './format';
import type { RingLeadIn } from './audio-sync';

/**
 * The marker at the very top of a transcript: the recording was running while
 * the phone was still ringing.
 *
 * WHY A TRANSCRIPT NEEDS THIS AT ALL. An outbound recording starts at egress,
 * which is before the pickup, and the ring is silent in the mix — no carrier
 * early media arrives, so the head of the file measures at the office-ambience
 * floor. A reviewer presses play, hears nothing for eight seconds, and reports
 * that the agent sat mute. On int `a61b4bf1` the greeting was on the wire 2.90s
 * after the pickup; the dead air in front of it was 8.00s of ring. This row is
 * the sentence that would have ended that report before it was written.
 *
 * "RINGING · 8.0s", NOT "UP TO". `SilenceGapRow` hedges because its interval
 * ends with a line being spoken, so the true silence is strictly shorter and
 * unknowable. Here both endpoints are measured instants with nothing but
 * silence between them, so the number IS the lead-in and hedging it would be
 * a different lie. It is also NOT the runtime's dial-to-pickup `tRingMs`,
 * which is about a second longer.
 *
 * Same full-width rule as `SilenceGapRow` and `CallEndedRow`, and for the same
 * reason: it is not a turn, and putting it in the conversation's left/right
 * rhythm would read as one.
 */
export function RingLeadInRow({
  leadIn,
  onSeek,
}: {
  leadIn: RingLeadIn;
  /** When wired, clicking skips the ring — the same affordance a bubble has. */
  onSeek?: ((relativeMs: number) => void) | undefined;
}): React.ReactElement {
  const label = (
    <span className="flex items-center gap-1 whitespace-nowrap">
      <BellRing className="h-3 w-3" />
      {`ringing · ${formatGapDuration(leadIn.durationMs)}`}
    </span>
  );
  return (
    <div
      data-ring-lead-in="true"
      className="flex items-center gap-2 py-0.5 text-[10px] text-muted-foreground"
    >
      <span className="h-px flex-1 bg-border" />
      {onSeek ? (
        <button
          type="button"
          onClick={() => onSeek(leadIn.durationMs)}
          title={`Play from ${formatDuration(leadIn.durationMs)}`}
          className="flex items-center gap-1 whitespace-nowrap rounded hover:text-foreground"
        >
          {label}
        </button>
      ) : (
        label
      )}
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}
